// =============================================================================
// Bureau de Matthieu (ex-Bureau AMO) — lots 1 à 3 (T-078, T-099, T-102). Matthieu est l'agent IA du métier AMO ; il signe les fiches.
// Pour chaque mail de amo@ : une FICHE (problème, réponse prête, à faire par JJ, fait par nous après envoi).
// Lot 2 : « Valider et envoyer » / « Modifier puis envoyer » appellent le SERVEUR, qui décide : mode capture (rien ne part)
// ou réel (production, tenant autorisé, seconde clé de module). L'écran ne décide jamais d'envoyer lui-même.
// Classement (CLASSER / SUPPRIMER) : liste « Traités » avec retour arrière par mail. Gestes de JJ : liste à cocher.
// Lot 3 : chaque mail entrant crée sa fiche ; trois voies (indésirable · doute · demande) ; « À trancher » = une question, 2 ou 3 boutons.
// Les montants sont LUS dans le dossier OmniHub, jamais calculés ici. Couleurs : tokens var(--oh-*) uniquement (2 modes).
// =============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { SavoirMatthieu } from "./BureauOptions";
import { Download, ExternalLink, Eye, Inbox, Link2, MessageSquare, Paperclip, Undo2, X } from "lucide-react";

import { OHGrid } from "../../../components/ui/oh-grid";
import { OhCard, OhCardHeader, OhField, OhInput, OhSelect, OhStatusNote } from "../../../components/ui/oh-charte";
import { TintedBadge } from "../../../components/ui/oh-tinted";
import * as api from "./bureauAmoApi";
import { neutraliserHtml } from "./mailHtmlSur";
import Bouton, { BOUTON } from "./boutonBureau";
import ParlerAMatthieu from "./ParlerAMatthieu";
import ReponsesPretes from "./ReponsesPretes";
import {
  BoutonRediger, CarteActions, CompteRendu, CreerDossier, DemanderPieces, ToujoursClasser, Transferer, messageEchec, useMatthieuConnecte,
} from "./ActionsFiche";
import {
  BandeauCompteurs, CarteFiche, EtatVide, NavFiltres, PastilleMode, RechercheFiches, SquelettePanneau, SqueletteListe,
  Puce, TYPE_EXPEDITEUR, URGENCE_PUCE, carteDepuisEntrant, carteDepuisFiche, libelleAction,
} from "./BureauMatthieuUi";

const URGENCE = URGENCE_PUCE;
const STATUT = {
  proposee: "À traiter", ok_enregistre: "Ok enregistré", envoi_en_cours: "Envoi à vérifier", echec_envoi: "Envoi échoué",
  envoyee: "Envoyée", capturee: "Capturée (rien envoyé)", refusee: "Refusée",
};
const STATUT_COULEUR = { proposee: "amber", ok_enregistre: "amber", envoi_en_cours: "orange", echec_envoi: "red", envoyee: "green", capturee: "cyan", refusee: "red" };
const ENVOYABLES = ["proposee", "ok_enregistre"];
const DETAIL_AFFICHE = [422, 429, 502, 503];
const NOTE_LIBELLE = { ajoutee: "ajoutée", sans_dossier: "aucun dossier rattaché", echec: "ÉCHEC (à ajouter à la main)" };
const COPIE_LIBELLE = { ok: "faite", echec: "ÉCHEC (à ranger à la main)", non_faite: "non faite" };

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso).slice(0, 10) : d.toLocaleDateString("fr-FR");
};
const fmtMontant = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v);

const titre = { fontWeight: 500, color: "var(--oh-text)", fontSize: 14, margin: 0 };
const muted = { color: "var(--oh-text-muted)", fontSize: 13 };

function Bandeau({ mode }) {
  if (mode === "reel") {
    return (
      <OhStatusNote variant="info" data-testid="bureau-amo-bandeau-reel" title="Envoi réel actif">
        « Valider et envoyer » envoie le mail depuis amo@ au nom de Transition Énergie France, tel que vous le lisez. Un envoi ne se reprend pas.
      </OhStatusNote>
    );
  }
  return (
    <OhStatusNote variant="info" data-testid="bureau-amo-bandeau-capture" title="Mode capture : aucun mail ne part">
      « Valider et envoyer » enregistre votre décision et la réponse figée au journal, sans rien envoyer. Le classement est simulé de la même façon.
    </OhStatusNote>
  );
}

function ModuleFerme() {
  return (
    <OhStatusNote variant="info" data-testid="bureau-amo-module-ferme" title="Bureau de Matthieu non activé">
      Ce module n'est pas activé pour votre entreprise, ou votre rôle ne permet pas de l'ouvrir.
    </OhStatusNote>
  );
}

// ───────────────────── T-280 : « nouveaux depuis votre dernière visite » ─────────────────────
const DUREE_VISITE_MS = 2000;
const libelleNouveaux = (n) => `${n} nouveau${n > 1 ? "x" : ""} mail${n > 1 ? "s" : ""}`;

function NouveauxMails() {
  const [etat, setEtat] = useState(null);
  useEffect(() => {
    let vivant = true;
    api.nouveaux().then((d) => { if (vivant) setEtat(d); }).catch(() => {});
    // La visite ne compte qu'après quelques secondes sur l'écran : un montage–démontage immédiat (rechargement, double rendu) ne la consomme pas.
    let comptee = false;
    const t = setTimeout(() => { comptee = true; }, DUREE_VISITE_MS);
    return () => { vivant = false; clearTimeout(t); if (comptee) api.marquerVisite().catch(() => {}); };
  }, []);
  if (!etat || typeof etat.nombre !== "number") return null;
  const n = etat.nombre;
  const phrase = etat.premiere_visite
    ? `${n} mail${n > 1 ? "s" : ""} reçu${n > 1 ? "s" : ""} ces 7 derniers jours`
    : `${libelleNouveaux(n)} depuis votre dernière visite`;
  return (
    <div className="flex flex-wrap items-center gap-2" style={{ marginBottom: "var(--oh-space-3)" }} data-testid="bureau-amo-nouveaux" role="status" aria-label={phrase}>
      <Puce color={n > 0 ? "blue" : "cyan"} data-testid="bureau-amo-nouveaux-valeur">{n > 0 ? phrase : "Aucun nouveau mail depuis votre dernière visite"}</Puce>
      {n > 0 && (
        <Bouton data-testid="bureau-amo-nouveaux-vus" aria-label="Marquer les nouveaux mails comme vus"
          onClick={async () => { try { await api.marquerVisite(); setEtat({ ...etat, nombre: 0, premiere_visite: false }); } catch { /* le compteur reste affiché */ } }}>
          Marquer comme vus
        </Bouton>
      )}
    </div>
  );
}

// ───────────────────────────── Écran « Réponses » : compteurs · filtres · cartes · panneau (T-215) ─────────────────────────────
const LIMITE = 50;
const AUTRES_ETATS = ["ok_enregistre", "envoi_en_cours", "echec_envoi", "refusee"];

function ListeAmo({ ouvertId, onOuvrir, onFermer, onOuvrirATrancher, onOuvrirWebmail, onOuvrirDossier, onOuvrirProposition, redactionPour, filtreInitial }) {
  const [vue, setVue] = useState(filtreInitial || "a_traiter");
  const [autre, setAutre] = useState("");
  const [saisie, setSaisie] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [liste, setListe] = useState({ chargement: true, erreur: null, donnees: null });
  const [syn, setSyn] = useState({ chargement: true, erreur: null, donnees: null });
  const [entrantOuvert, setEntrantOuvert] = useState(null);
  const numeroListe = useRef(0);
  const numeroSyn = useRef(0);
  const publicites = vue === "publicites" && !autre;
  const chaine = vue === "pret_a_envoyer" && !autre;          // T-281b : la vue à la chaîne de T-281 sur les seules réponses prêtes à envoyer

  useEffect(() => {
    const t = setTimeout(() => { setQ(saisie.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [saisie]);

  const charger = useCallback(async () => {
    if (chaine) return;
    const n = ++numeroListe.current;
    setListe((e) => ({ ...e, chargement: true, erreur: null }));
    try {
      const donnees = publicites
        ? await api.listerEntrants({ publicite: true, page, limite: LIMITE })
        : await api.lister({ ...(autre ? { statut: autre } : { vue }), tri: "urgence", sens: "asc", q, page, limite: LIMITE });
      if (n === numeroListe.current) setListe({ chargement: false, erreur: null, donnees });
    } catch (e) {
      if (n === numeroListe.current) setListe({ chargement: false, erreur: e, donnees: null });
    }
  }, [vue, autre, q, page, publicites, chaine]);
  useEffect(() => { charger(); }, [charger]);

  const chargerSynthese = useCallback(async () => {
    const n = ++numeroSyn.current;
    setSyn((e) => ({ ...e, chargement: true, erreur: null }));
    try {
      const donnees = await api.synthese();
      if (n === numeroSyn.current) setSyn({ chargement: false, erreur: null, donnees });
    } catch (e) {
      if (n === numeroSyn.current) setSyn({ chargement: false, erreur: e, donnees: null });
    }
  }, []);
  useEffect(() => { chargerSynthese(); }, [chargerSynthese]);

  const rafraichir = useCallback(() => { charger(); chargerSynthese(); }, [charger, chargerSynthese]);
  const nbPret = syn.donnees?.filtres?.pret_a_envoyer;
  const comptePret = useCallback((n) => { if (n !== nbPret) chargerSynthese(); }, [nbPret, chargerSynthese]);

  if ([liste.erreur, syn.erreur].some((e) => e && [401, 403].includes(e.status))) return <ModuleFerme />;

  const items = liste.donnees?.items || [];
  const total = liste.donnees?.total ?? 0;
  const cartes = items.map(publicites ? carteDepuisEntrant : carteDepuisFiche);
  const actifId = publicites ? entrantOuvert : ouvertId;
  const mode = liste.donnees?.mode || syn.donnees?.mode;
  const comptes = liste.donnees?.comptes || {};
  const ouvrir = (id) => (publicites ? setEntrantOuvert(id) : onOuvrir(id));
  const ouvrirProposition = (pid, redaction) => {
    setVue("a_traiter"); setAutre(""); setPage(1); setEntrantOuvert(null);
    if (onOuvrirProposition) onOuvrirProposition(pid, redaction); else onOuvrir(pid);
    rafraichir();
  };
  const choisirVue = (cle) => { setVue(cle); setAutre(""); setPage(1); setEntrantOuvert(null); };
  const debut = total === 0 ? 0 : (page - 1) * LIMITE + 1;
  const fin = Math.min(page * LIMITE, total);

  return (
    <div className="oh-bm-racine" data-testid="bureau-amo-liste">
      <div className="space-y-3">
        {mode && <div><PastilleMode mode={mode} /></div>}
        <BandeauCompteurs synthese={syn.donnees} chargement={syn.chargement && !syn.donnees} erreur={syn.erreur} onRecharger={chargerSynthese}
          onOuvrirATrancher={onOuvrirATrancher} />
        <div className="oh-bm-layout">
          <aside className="space-y-3" aria-label="Navigation des fiches">
            <NavFiltres actif={autre ? null : vue} onChoisir={choisirVue} compteurs={syn.donnees?.filtres} chargement={syn.chargement} />
            <OhField label="Autres états" htmlFor="bureau-amo-filtre-statut">
              <OhSelect id="bureau-amo-filtre-statut" data-testid="bureau-amo-filtre-statut" value={autre}
                onChange={(e) => { setAutre(e.target.value); setPage(1); setEntrantOuvert(null); }}>
                <option value="">Aucun</option>
                {AUTRES_ETATS.map((k) => <option key={k} value={k}>{`${STATUT[k]} (${comptes[k] ?? 0})`}</option>)}
              </OhSelect>
            </OhField>
          </aside>

          {chaine ? (
            <section className="oh-bm-pleine" aria-label="Réponses prêtes à envoyer" data-testid="bureau-amo-pret-a-envoyer">
              <ReponsesPretes filtre="pret_a_envoyer" MailRecu={MailRecu} onOuvrirWebmail={onOuvrirWebmail} onCompte={comptePret}
                onOuvrir={(id) => { choisirVue("a_traiter"); onOuvrir(id); }} />
            </section>
          ) : (<>
          <section className="space-y-3" aria-label="Liste des fiches" aria-busy={liste.chargement} data-testid="bureau-amo-cartes">
            <RechercheFiches valeur={saisie} onChange={setSaisie} desactivee={publicites} />
            {liste.erreur && (
              <OhStatusNote variant="error" data-testid="bureau-amo-erreur-liste" title="Chargement impossible">
                <div className="flex flex-wrap items-center gap-2">
                  <span>{typeof liste.erreur.detail === "string" ? liste.erreur.detail : "Réessayez dans un instant."}</span>
                  <Bouton data-testid="bureau-amo-liste-recharger" onClick={charger}>Réessayer</Bouton>
                </div>
              </OhStatusNote>
            )}
            {liste.chargement && !liste.donnees ? <SqueletteListe /> : (
              !liste.erreur && cartes.length === 0 ? <EtatVide filtre={publicites ? "publicites" : autre || vue} /> : (
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: "var(--oh-space-2)" }}>
                  {cartes.map((c) => <li key={c.id}><CarteFiche carte={c} actif={c.id === actifId} onOuvrir={ouvrir} /></li>)}
                </ul>
              )
            )}
            {total > LIMITE && (
              <div className="flex flex-wrap items-center justify-between gap-2" data-testid="bureau-amo-pagination">
                <span style={muted}>{debut}–{fin} sur {total}</span>
                <div className="flex gap-2">
                  <Bouton data-testid="bureau-amo-page-precedente" disabled={page <= 1 || liste.chargement} onClick={() => setPage((p) => p - 1)}>Précédentes</Bouton>
                  <Bouton data-testid="bureau-amo-page-suivante" disabled={fin >= total || liste.chargement} onClick={() => setPage((p) => p + 1)}>Suivantes</Bouton>
                </div>
              </div>
            )}
          </section>

          <section aria-label="Fiche ouverte" data-testid="bureau-amo-panneau">
            {publicites && entrantOuvert ? (
              <FicheMatthieu id={entrantOuvert} onFerme={() => setEntrantOuvert(null)} onChange={rafraichir} onOuvrirProposition={ouvrirProposition} />
            ) : !publicites && ouvertId ? (
              <FicheAmo id={ouvertId} onRetour={onFermer} onOuvrirWebmail={onOuvrirWebmail} onChange={rafraichir} copieCachee={syn.donnees?.copie_cachee}
                onOuvrirProposition={ouvrirProposition} redactionAuto={redactionPour} onOuvrirDossier={onOuvrirDossier} />
            ) : (
              <p className="oh-bm-pasdefiche" style={{ ...muted, padding: "var(--oh-space-8) var(--oh-space-4)", textAlign: "center", border: "1px dashed var(--oh-border)", borderRadius: "var(--oh-radius-card)" }}
                data-testid="bureau-amo-panneau-vide">
                Choisissez une fiche pour lire la demande et préparer la réponse.
              </p>
            )}
          </section>
          </>)}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────── Fiche détaillée ───────────────────────────────
function Section({ titre: t, testid, children }) {
  return (
    <OhCard data-testid={testid}>
      <OhCardHeader title={t} />
      <div className="space-y-2" style={{ marginTop: "var(--oh-space-3)" }}>{children}</div>
    </OhCard>
  );
}

const fmtDateHeure = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso).slice(0, 16) : d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
};
const fmtTaille = (n) => (n < 1024 ? `${n} o` : n < 1048576 ? `${Math.round(n / 1024)} Ko` : `${(n / 1048576).toFixed(1)} Mo`);
const personne = (p) => (p && (p.nom ? `${p.nom} <${p.adresse}>` : p.adresse)) || "—";
const personnes = (liste) => (liste && liste.length ? liste.map(personne).join(", ") : "—");
const ZONE_MAIL = { margin: 0, fontFamily: "inherit", fontSize: 14, lineHeight: 1.6, color: "var(--oh-text)", background: "var(--oh-bg-surface)", border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-control)", padding: "var(--oh-space-3)", maxHeight: "70vh", overflow: "auto", overflowWrap: "anywhere" };

// T-239 — le mail AUQUEL on répond, en entier, au-dessus de la réponse proposée. Source : le message dans amo@ (lecture seule côté serveur) ;
// à défaut, l'extrait conservé dans la fiche, avec la mention du serveur. Le HTML est assaini par le serveur PUIS reconstruit ici.
function MailRecu({ fiche: f, mail, onRecharger, onOuvrirWebmail }) {
  const [filOuvert, setFilOuvert] = useState(false);
  const d = mail.donnees;
  const complet = Boolean(d && d.trouve);
  const repli = d && !d.trouve ? d : null;
  const m = complet ? d : {
    expediteur: { nom: "", adresse: f.expediteur_adresse }, destinataires: [], copie: [], date: f.date_mail, objet: f.objet_origine,
    texte: f.mail_extrait || "", html: null, images_masquees: 0, pieces: [], fil: repli?.fil || [], webmail: null,
  };
  const texteRepli = repli ? (repli.texte ?? "") : (m.texte || "");
  const html = useMemo(() => (complet && m.html ? neutraliserHtml(m.html) : ""), [complet, m.html]);
  const fil = m.fil || [];
  const sansTexte = !html && !String(complet ? m.texte : texteRepli).trim();

  return (
    <Section titre="Mail reçu" testid="bureau-amo-mail">
      <p style={titre} data-testid="bureau-amo-mail-objet">{m.objet || f.objet_origine}</p>
      <dl className="space-y-1" style={{ margin: 0, fontSize: 14 }} data-testid="bureau-amo-mail-entetes">
        <div className="flex flex-wrap gap-2"><dt style={{ ...muted, minWidth: 72 }}>De</dt><dd style={{ margin: 0, color: "var(--oh-text)", overflowWrap: "anywhere" }} data-testid="bureau-amo-mail-de">{personne(m.expediteur)}</dd></div>
        <div className="flex flex-wrap gap-2"><dt style={{ ...muted, minWidth: 72 }}>À</dt><dd style={{ margin: 0, color: "var(--oh-text)", overflowWrap: "anywhere" }} data-testid="bureau-amo-mail-a">{complet ? personnes(m.destinataires) : "—"}</dd></div>
        {complet && m.copie?.length > 0 && (
          <div className="flex flex-wrap gap-2"><dt style={{ ...muted, minWidth: 72 }}>Copie</dt><dd style={{ margin: 0, color: "var(--oh-text)", overflowWrap: "anywhere" }} data-testid="bureau-amo-mail-cc">{personnes(m.copie)}</dd></div>
        )}
        <div className="flex flex-wrap gap-2"><dt style={{ ...muted, minWidth: 72 }}>Date</dt><dd style={{ margin: 0, color: "var(--oh-text)" }} data-testid="bureau-amo-mail-date">{fmtDateHeure(m.date)}</dd></div>
        <div className="flex flex-wrap gap-2"><dt style={{ ...muted, minWidth: 72 }}>Type</dt><dd style={{ margin: 0, color: "var(--oh-text)" }}>{TYPE_EXPEDITEUR[f.expediteur_type] || f.expediteur_type || "—"}</dd></div>
      </dl>

      {mail.chargement && <p style={muted} data-testid="bureau-amo-mail-chargement">Lecture du mail complet dans amo@… En attendant, l'extrait conservé dans la fiche.</p>}
      {mail.erreur && (
        <OhStatusNote variant="error" data-testid="bureau-amo-mail-erreur" title="Le mail complet n'a pas pu être chargé">
          <div className="space-y-2">
            <p style={{ margin: 0 }}>L'extrait conservé dans la fiche est affiché à la place.</p>
            <Bouton data-testid="bureau-amo-mail-recharger" onClick={onRecharger}>Réessayer</Bouton>
          </div>
        </OhStatusNote>
      )}
      {repli && (
        <OhStatusNote variant={repli.raison === "imap_indisponible" ? "error" : "info"} data-testid="bureau-amo-mail-repli" title={repli.mention || "Mail introuvable dans amo@"}>
          <div className="space-y-2">
            <p style={{ margin: 0 }}>L'extrait conservé dans la fiche est affiché à la place.</p>
            {repli.raison === "imap_indisponible" && <Bouton data-testid="bureau-amo-mail-recharger" onClick={onRecharger}>Réessayer</Bouton>}
          </div>
        </OhStatusNote>
      )}

      {html ? (
        <div data-testid="bureau-amo-mail-html" style={ZONE_MAIL} dangerouslySetInnerHTML={{ __html: html }} />
      ) : sansTexte && !complet && !(f.mail_extrait || "").trim() ? (
        <p style={muted} data-testid="bureau-amo-extrait-purge">L'extrait du mail a dépassé sa durée de conservation et a été retiré.</p>
      ) : sansTexte ? (
        <p style={muted} data-testid="bureau-amo-mail-vide">Ce mail ne contient pas de texte.</p>
      ) : (
        <pre data-testid={complet ? "bureau-amo-mail-texte" : "bureau-amo-extrait"} style={{ ...ZONE_MAIL, whiteSpace: "pre-wrap" }}>{complet ? m.texte : texteRepli}</pre>
      )}
      {complet && m.images_masquees > 0 && (
        <p style={muted} data-testid="bureau-amo-mail-images">
          {m.images_masquees} image{m.images_masquees > 1 ? "s" : ""} du mail non affichée{m.images_masquees > 1 ? "s" : ""} (elles signaleraient votre lecture à l'expéditeur).
        </p>
      )}

      {complet && m.pieces?.length > 0 && (
        <div className="space-y-1" data-testid="bureau-amo-mail-pieces">
          <p style={titre}><Paperclip className="w-4 h-4 inline mr-1" />Pièces jointes ({m.pieces.length})</p>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }} className="space-y-1">
            {m.pieces.map((p) => (
              <li key={p.index} className="flex flex-wrap items-center justify-between gap-2" style={{ fontSize: 14, color: "var(--oh-text)" }}>
                <span style={{ overflowWrap: "anywhere" }}>{p.nom} <span style={muted}>· {fmtTaille(p.taille)}{p.inline ? " · intégrée au texte" : ""}</span></span>
                <a href={api.urlPieceMail(f.id, p.index)} download data-testid={`bureau-amo-mail-piece-${p.index}`} aria-label={`Télécharger la pièce jointe ${p.nom}`}
                  rel="noopener" style={{ ...BOUTON.base, ...BOUTON.discret, display: "inline-flex", alignItems: "center", textDecoration: "none" }}>
                  <Download className="w-4 h-4 inline mr-1" />Télécharger
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {fil.length > 0 ? (
        <div className="space-y-2" data-testid="bureau-amo-mail-fil">
          <Bouton data-testid="bureau-amo-mail-fil-bascule" aria-expanded={filOuvert} onClick={() => setFilOuvert((o) => !o)}>
            {filOuvert ? "Masquer" : "Afficher"} le fil de l'échange ({fil.length} mail{fil.length > 1 ? "s" : ""} précédent{fil.length > 1 ? "s" : ""})
          </Bouton>
          {filOuvert && (
            <ol style={{ listStyle: "none", padding: 0, margin: 0 }} className="space-y-2">
              {fil.map((e, i) => (
                <li key={`${e.message_id}-${i}`} data-testid={`bureau-amo-mail-fil-${i}`} className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <TintedBadge color={e.sens === "envoye" ? "green" : "blue"}>{e.sens === "envoye" ? "Envoyé" : "Reçu"}</TintedBadge>
                    <span style={muted}>{fmtDateHeure(e.date)} · {personne(e.expediteur)}</span>
                  </div>
                  {e.objet && <p style={titre}>{e.objet}</p>}
                  <pre style={{ ...ZONE_MAIL, whiteSpace: "pre-wrap", maxHeight: "40vh" }}>{e.texte || "(sans texte)"}</pre>
                </li>
              ))}
            </ol>
          )}
        </div>
      ) : complet && <p style={muted} data-testid="bureau-amo-mail-sans-fil">Aucun mail précédent dans cet échange.</p>}

      {complet && m.webmail && onOuvrirWebmail && (
        <div>
          <Bouton type="secondaire" data-testid="bureau-amo-mail-webmail" onClick={() => onOuvrirWebmail(m.webmail)}>
            <ExternalLink className="w-4 h-4 inline mr-1" />Ouvrir dans le webmail
          </Bouton>
        </div>
      )}
    </Section>
  );
}

function ConfianceAnalyse({ confiance }) {
  if (confiance == null) return <p style={muted} data-testid="bureau-amo-confiance-absente">Confiance : non mesurée pour cette fiche.</p>;
  return (
    <div className="flex items-center gap-2" data-testid="bureau-amo-confiance">
      <span style={muted}>Confiance de l'analyse</span>
      <div role="progressbar" aria-label={`Confiance de l'analyse : ${confiance} %`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={confiance}
        style={{ flex: "0 1 120px", height: 6, borderRadius: 999, background: "var(--oh-border)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${confiance}%`, background: "var(--oh-accent)" }} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 500, color: "var(--oh-text)" }} data-testid="bureau-amo-confiance-valeur">{confiance} %</span>
    </div>
  );
}

function LigneEnvoi({ mode, copieCachee }) {
  const adresse = copieCachee?.adresse;
  const origine = copieCachee?.source === "parametre" ? "adresse de suivi de l'entreprise" : "votre compte";
  return (
    <p style={{ ...muted, margin: 0 }} data-testid="bureau-amo-envoi-ligne">
      {mode === "reel" ? "Envoi réel : le mail part depuis amo@ au nom de Transition Énergie France et ne se reprend pas. " : "Mode capture : rien ne part, la décision est seulement journalisée. "}
      {adresse
        ? <span data-testid="bureau-amo-copie-cachee">{mode === "reel" ? "Copie cachée envoyée à " : "En envoi réel, une copie cachée serait envoyée à "}{adresse} ({origine}).</span>
        : <span data-testid="bureau-amo-copie-cachee">Aucune adresse de copie cachée trouvée pour votre compte.</span>}
    </p>
  );
}

function FicheAmo({ id, onRetour, onOuvrirWebmail, onChange, copieCachee, onOuvrirProposition, redactionAuto, onOuvrirDossier, onCorrigee, texteInitial }) {   // T-459 : onCorrigee(texte) après « Modifier puis envoyer » (le Bureau propose d'apprendre)
  const [etat, setEtat] = useState({ chargement: true, erreur: null, fiche: null });
  const [texte, setTexte] = useState("");
  const [objet, setObjet] = useState("");
  const [refus, setRefus] = useState({ ouvert: false, motif: "" });
  const [dossierQ, setDossierQ] = useState("");
  const [candidats, setCandidats] = useState([]);
  const [enCours, setEnCours] = useState(null);
  const [message, setMessage] = useState(null);
  const [confirme, setConfirme] = useState(false);
  const [mail, setMail] = useState({ chargement: true, erreur: null, donnees: null });
  const [rechargeMail, setRechargeMail] = useState(0);
  const [parler, setParler] = useState(false);
  const [rediger, setRediger] = useState(false);
  const racine = useRef(null);
  const occupe = enCours !== null;
  const fichePrete = etat.fiche;
  const connecte = useMatthieuConnecte(Boolean(fichePrete) && ENVOYABLES.includes(fichePrete.statut) && !(fichePrete.texte_propose || "").trim());
  useEffect(() => { setRediger(Boolean(redactionAuto) && redactionAuto === id); }, [id, redactionAuto]);

  useEffect(() => {
    if (racine.current && typeof racine.current.scrollIntoView === "function") racine.current.scrollIntoView({ block: "start" });
  }, [id]);

  useEffect(() => {
    let actif = true;
    setMail({ chargement: true, erreur: null, donnees: null });
    api.mailOrigine(id).then(
      (d) => { if (actif) setMail({ chargement: false, erreur: null, donnees: d }); },
      (e) => { if (actif) setMail({ chargement: false, erreur: e, donnees: null }); },
    );
    return () => { actif = false; };
  }, [id, rechargeMail]);

  const charger = useCallback(async () => {
    setEtat((e) => ({ ...e, chargement: true, erreur: null }));
    try {
      const f = await api.fiche(id);
      setEtat({ chargement: false, erreur: null, fiche: f });
      setTexte(texteInitial || f.texte_propose || "");      // T-459 : la réponse habituelle apprise PRÉ-REMPLIT l'éditeur ; rien ne part sans « Modifier puis envoyer »
      setObjet(f.objet_propose || "");
      setConfirme(false);
    } catch (e) {
      setEtat({ chargement: false, erreur: e, fiche: null });
    }
  }, [id, texteInitial]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setMessage(null); setRefus({ ouvert: false, motif: "" }); charger(); }, [charger]);

  const agir = async (action, succes, nature = "autre") => {
    setEnCours(nature);
    setMessage(null);
    try {
      const retour = await action();
      setMessage(typeof succes === "function" ? succes(retour) : succes);
      setRefus({ ouvert: false, motif: "" });
      await charger();
      if (onChange) onChange();
    } catch (e) {
      if (e.status === 409) {
        toast.error("La fiche a changé entre-temps : elle est rechargée, relisez avant de décider.");
        await charger();
      } else if (DETAIL_AFFICHE.includes(e.status)) {
        toast.error(typeof e.detail === "string" ? e.detail : "Données refusées par le serveur.");
        if (e.status === 502) { await charger(); if (onChange) onChange(); }
      } else {
        toast.error("Enregistrement impossible.");
      }
    } finally {
      setEnCours(null);
    }
  };

  const cocher = async (index, fait) => {
    try {
      const r = await api.cocherGeste(id, index, fait);
      setEtat((e) => ({ ...e, fiche: { ...e.fiche, gestes_jj: r.gestes_jj } }));
    } catch {
      toast.error("Impossible d'enregistrer la case cochée.");
    }
  };

  const BoutonFermer = (
    <Bouton data-testid="bureau-amo-retour" aria-label="Fermer la fiche" onClick={onRetour}><X className="w-4 h-4 inline mr-1" aria-hidden="true" />Fermer la fiche</Bouton>
  );

  if (etat.erreur) {
    return (
      <div className="space-y-3" ref={racine}>
        {BoutonFermer}
        {[401, 403].includes(etat.erreur.status) ? <ModuleFerme /> : (
          <OhStatusNote variant="error" data-testid="bureau-amo-erreur-fiche" title="Fiche introuvable">
            <div className="flex flex-wrap items-center gap-2">
              <span>Cette fiche n'existe pas ou n'est plus disponible.</span>
              <Bouton data-testid="bureau-amo-fiche-recharger" onClick={charger}>Réessayer</Bouton>
            </div>
          </OhStatusNote>
        )}
      </div>
    );
  }
  const f = etat.fiche;
  if (!f) return <div ref={racine}><SquelettePanneau /></div>;

  const aTraiter = ENVOYABLES.includes(f.statut);
  const rattachable = f.statut !== "envoi_en_cours";   // T-362 : on rattache aussi une fiche déjà envoyée, refusée ou échouée (jamais pendant l'envoi)
  const mode = f.mode === "reel" ? "reel" : "capture";
  const sansReponse = !(f.texte_propose || "").trim();
  const modifie = texte !== (f.texte_propose || "") || objet !== (f.objet_propose || "");
  const manqueConfirmation = Boolean(f.reply_to_different) && !confirme;
  const destinataireConfirme = f.reply_to_different && confirme ? f.destinataire_propose : undefined;
  const messageEnvoi = (r) => (r?.mode === "reel"
    ? "Mail envoyé depuis amo@. La note au dossier et la copie dans Envoyés sont indiquées plus bas."
    : "Capturé : rien n'est parti (mode capture). La réponse figée est au journal.");
  const urgence = URGENCE[f.urgence];

  return (
    <div className="space-y-3" data-testid="bureau-amo-fiche" ref={racine} aria-busy={enCours === "envoi"}>
      <div className="flex flex-wrap items-center gap-2">
        {BoutonFermer}
        {urgence ? <Puce color={urgence[0]}>{urgence[1]}</Puce> : f.urgence ? <Puce color="blue">{f.urgence}</Puce> : null}
        <Puce color={STATUT_COULEUR[f.statut] || "blue"} data-testid="bureau-amo-statut">{STATUT[f.statut] || f.statut}</Puce>
        <Bouton type="secondaire" data-testid="bureau-amo-parler" style={{ marginLeft: "auto" }} onClick={() => setParler(true)}>
          <MessageSquare className="w-4 h-4 inline mr-1" aria-hidden="true" />Parler à Matthieu
        </Bouton>
      </div>
      {parler && <ParlerAMatthieu cible={{ type: "fiche", id: f.id }} titre="Parler à Matthieu de ce mail" onFerme={() => setParler(false)} onExecutee={() => { charger(); if (onChange) onChange(); }} />}
      {rediger && !parler && (
        <ParlerAMatthieu cible={{ type: "fiche", id: f.id }} titre="Rédiger la réponse avec Matthieu" onFerme={() => setRediger(false)}
          messageInitial="Rédige la réponse à ce mail et propose le texte dans la fiche (action « modifier_reponse »). N'envoie rien : je relis, je modifie puis je valide."
          onExecutee={() => { charger(); if (onChange) onChange(); }} />
      )}
      {message && <OhStatusNote variant="success" data-testid="bureau-amo-message" title={message} />}

      <div style={{ display: "grid", gap: "var(--oh-space-1)" }} data-testid="bureau-amo-fiche-entete">
        <h3 data-testid="bureau-amo-fiche-titre" style={{ margin: 0, fontSize: 18, fontWeight: 500, color: "var(--oh-text)", overflowWrap: "anywhere" }}>{f.objet_origine || "Sans objet"}</h3>
        <p data-testid="bureau-amo-fiche-contexte" style={muted}>
          {[f.client || "Client non rattaché", f.instructeur ? `Instructeur : ${f.instructeur}` : null, f.date_mail ? `reçu le ${fmtDate(f.date_mail)}` : null].filter(Boolean).join(" · ")}
        </p>
      </div>

      <Section titre="Demande et analyse" testid="bureau-amo-probleme">
        <p style={{ color: "var(--oh-text)", fontSize: 14, margin: 0, overflowWrap: "anywhere" }} data-testid="bureau-amo-demande">
          <span style={titre}>Demande : </span>{f.demande || "Non renseignée."}
        </p>
        <p style={{ color: "var(--oh-text)", fontSize: 14, margin: 0, overflowWrap: "anywhere" }} data-testid="bureau-amo-analyse">
          <span style={titre}>Analyse : </span>{f.probleme || "Non renseignée."}
        </p>
        <ConfianceAnalyse confiance={f.analyse?.confiance ?? null} />
        <p style={muted} data-testid="bureau-amo-signature">Fiche de Matthieu</p>
      </Section>

      <Section titre="Action préparée" testid="bureau-amo-actions-apres-ok">
        {(f.actions_apres_ok || []).length > 0 && (
          <ul style={{ margin: 0, paddingLeft: "var(--oh-space-4)", color: "var(--oh-text)", fontSize: 14 }}>
            {f.actions_apres_ok.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        )}
        <p style={muted} data-testid="bureau-amo-apres-envoi">
          {f.statut === "envoyee"
            ? `Note au dossier : ${NOTE_LIBELLE[f.note_dossier] || "non renseignée"} · Copie dans Envoyés : ${COPIE_LIBELLE[f.copie_envoyes] || "non renseignée"}.`
            : "À l'envoi, une note est ajoutée au dossier et une copie est rangée dans Envoyés de amo@."}
        </p>
        <p style={muted}>Les autres actions listées ne sont pas exécutées par OmniHub.</p>
      </Section>

      <MailRecu fiche={f} mail={mail} onRecharger={() => setRechargeMail((n) => n + 1)} onOuvrirWebmail={onOuvrirWebmail} />

      <Section titre="Réponse prête" testid="bureau-amo-reponse">
        <p style={muted} data-testid="bureau-amo-destinataire">À : {f.destinataire_propose || "—"}</p>
        {f.reply_to_different && (
          <OhStatusNote variant="error" data-testid="bureau-amo-alerte-reply-to" title="Adresse de réponse différente">
            Le mail demande qu'on réponde à une autre adresse que l'expéditeur. Le message part à l'adresse « À » ci-dessus, jamais à l'adresse de réponse.
          </OhStatusNote>
        )}
        {f.reply_to_different && aTraiter && (
          <label className="flex items-center gap-2" style={{ color: "var(--oh-text)", fontSize: 14 }}>
            <input type="checkbox" data-testid="bureau-amo-confirmer-destinataire" checked={confirme} onChange={(e) => setConfirme(e.target.checked)} />
            Je confirme que le destinataire est bien {f.destinataire_propose}
          </label>
        )}
        {sansReponse && aTraiter ? (
          <div className="space-y-2">
            <p style={muted} data-testid="bureau-amo-sans-reponse">Aucune réponse proposée pour ce mail : faites-la rédiger par Matthieu, ou classez-le sans répondre, avec un motif.</p>
            <BoutonRediger connecte={connecte} occupe={false} onClick={() => setRediger(true)} />
          </div>
        ) : (
          <>
            <OhField label="Objet" htmlFor="bureau-amo-objet">
              <OhInput id="bureau-amo-objet" data-testid="bureau-amo-objet" value={objet} disabled={!aTraiter || occupe} onChange={(e) => setObjet(e.target.value)} />
            </OhField>
            <OhField label="Texte de la réponse" htmlFor="bureau-amo-texte">
              <textarea id="bureau-amo-texte" data-testid="bureau-amo-texte" value={texte} disabled={!aTraiter || occupe} rows={10}
                onChange={(e) => setTexte(e.target.value)}
                style={{ width: "100%", padding: "var(--oh-space-3)", borderRadius: "var(--oh-radius-control)", border: "0.5px solid var(--oh-border)", background: "var(--oh-bg-elevated)", color: "var(--oh-text)", fontSize: 14, lineHeight: 1.6 }} />
            </OhField>
          </>
        )}

        {aTraiter && (
          <>
            <div className="flex flex-wrap items-center gap-2" data-testid="bureau-amo-decisions">
              <Bouton type="primaire" data-testid="bureau-amo-valider" disabled={occupe || sansReponse || modifie || manqueConfirmation}
                aria-busy={enCours === "envoi"}
                title={modifie ? "Texte modifié : utilisez « Modifier puis envoyer »" : manqueConfirmation ? "Confirmez d'abord le destinataire" : undefined}
                onClick={() => agir(() => api.valider(f.id, f.version, f.texte_sha256, destinataireConfirme), messageEnvoi, "envoi")}>
                {enCours === "envoi" ? "Envoi en cours…" : "Valider et envoyer"}
              </Bouton>
              <Bouton type="secondaire" data-testid="bureau-amo-modifier-envoyer" disabled={occupe || sansReponse || !modifie || !texte.trim() || manqueConfirmation}
                onClick={() => agir(() => api.modifierEtEnvoyer(f.id, f.version, texte, objet !== (f.objet_propose || "") ? objet : undefined, destinataireConfirme).then((r) => { if (onCorrigee) onCorrigee(texte, f); return r; }), messageEnvoi, "envoi")}>
                Modifier puis envoyer
              </Bouton>
              <Bouton data-testid="bureau-amo-classer" disabled={occupe} aria-expanded={refus.ouvert} onClick={() => setRefus((r) => ({ ...r, ouvert: !r.ouvert }))}>
                Classer sans répondre
              </Bouton>
            </div>
            <LigneEnvoi mode={mode} copieCachee={copieCachee} />
          </>
        )}
        {f.statut === "envoi_en_cours" && (
          <div className="space-y-2" data-testid="bureau-amo-verifier">
            <OhStatusNote variant="error" title="Envoi à vérifier">
              Le serveur n'a pas confirmé l'envoi. Ne renvoyez rien : regardez dans « Envoyés » de amo@, puis indiquez le résultat.
            </OhStatusNote>
            <div className="flex flex-wrap items-center gap-2">
              <Bouton type="secondaire" data-testid="bureau-amo-verifie-envoye" disabled={occupe}
                onClick={() => agir(() => api.verifierEnvoi(f.id, f.version, "envoye"), "Noté : le mail est bien parti.")}>
                Le mail est bien parti
              </Bouton>
              <Bouton data-testid="bureau-amo-verifie-non-envoye" disabled={occupe}
                onClick={() => agir(() => api.verifierEnvoi(f.id, f.version, "non_envoye"), "Noté : le mail n'est pas parti. Vous pouvez réessayer.")}>
                Le mail n'est pas parti
              </Bouton>
            </div>
          </div>
        )}
        {f.statut === "echec_envoi" && (
          <div className="space-y-2" data-testid="bureau-amo-echec">
            <OhStatusNote variant="error" title="Le mail n'est pas parti">
              Le serveur n'a rien accepté{f.envoi?.erreur ? ` (${f.envoi.erreur})` : ""}. Vous pouvez réessayer.
            </OhStatusNote>
            <Bouton type="secondaire" data-testid="bureau-amo-reessayer" disabled={occupe}
              onClick={() => agir(() => api.reessayer(f.id, f.version), messageEnvoi, "envoi")}>
              {enCours === "envoi" ? "Envoi en cours…" : "Réessayer l'envoi"}
            </Bouton>
          </div>
        )}
        {aTraiter && refus.ouvert && (
          <div className="space-y-2" data-testid="bureau-amo-refus">
            <OhField label="Motif du classement" htmlFor="bureau-amo-motif" hint="Entre 3 et 500 caractères. Aucune réponse n'est envoyée.">
              <OhInput id="bureau-amo-motif" data-testid="bureau-amo-motif" value={refus.motif} maxLength={500}
                onChange={(e) => setRefus((r) => ({ ...r, motif: e.target.value }))} />
            </OhField>
            <Bouton data-testid="bureau-amo-refus-confirmer" disabled={occupe || refus.motif.trim().length < 3}
              onClick={() => agir(() => api.enregistrerRefus(f.id, f.version, refus.motif.trim()), "Fiche classée sans réponse.")}>
              Confirmer le classement
            </Bouton>
          </div>
        )}
      </Section>

      <CarteActions titre="Autres actions" testid="bureau-amo-autres-actions">
        {f.expediteur_type === "instructeur" && !f.sortante && (
          <div className="space-y-3" data-testid="bureau-amo-actions-instructeur">
            {!f.dossier && <p style={muted} data-testid="bureau-amo-actions-sans-dossier">Rattachez d'abord ce mail à un dossier (section « Dossier OmniHub ») pour ajouter un compte rendu ou demander des pièces.</p>}
            <CompteRendu id={f.id} desactive={!f.dossier || !aTraiter} onFait={charger} />
            <DemanderPieces id={f.id} desactive={!f.dossier || !aTraiter} onOuvrirProposition={(pid) => onOuvrirProposition && onOuvrirProposition(pid, false)} />
          </div>
        )}
        {typeof f.lead_type === "string" && <CreerDossier id={f.id} dejaDossierId={f.dossier_cree_id} onFait={async () => { await charger(); if (onChange) onChange(); }} />}
        <Transferer type="a-traiter" id={f.id} transfereA={f.transfere_a} desactive={!aTraiter} onFait={charger} />
      </CarteActions>

      <Section titre="À faire par JJ" testid="bureau-amo-gestes">
        {(f.gestes_jj || []).length === 0 ? <p style={muted}>Rien à faire de votre côté pour ce mail.</p> : (
          <ul className="space-y-3" style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {f.gestes_jj.map((g, i) => (
              <li key={i} className="space-y-1" data-testid={`bureau-amo-geste-${i}`}>
                <label className="flex items-center gap-2" style={titre}>
                  <input type="checkbox" data-testid={`bureau-amo-geste-case-${i}`} checked={Boolean(g.fait)} onChange={(e) => cocher(i, e.target.checked)} />
                  <span style={g.fait ? { textDecoration: "line-through", color: "var(--oh-text-muted)" } : undefined}>{g.libelle}</span>
                </label>
                {g.lien && <a href={g.lien} target="_blank" rel="noopener noreferrer" style={{ color: "var(--oh-accent-text)", fontSize: 14 }}>Ouvrir le lien</a>}
                {g.texte_a_coller && (
                  <pre style={{ whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit", fontSize: 14, color: "var(--oh-text)", background: "var(--oh-bg-surface)", border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-control)", padding: "var(--oh-space-3)" }}>
                    {g.texte_a_coller}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section titre="Dossier OmniHub" testid="bureau-amo-dossier">
        {f.dossier ? (
          <>
            <p style={{ color: "var(--oh-text)", fontSize: 14 }} data-testid="bureau-amo-dossier-ref">
              {f.dossier.reference} · statut {f.dossier.statut}
            </p>
            {onOuvrirDossier && f.dossier.id && (
              <div className="flex flex-wrap gap-2" data-testid="bureau-amo-dossier-liens">
                <Bouton data-testid="bureau-amo-ouvrir-dossier" aria-label={`Ouvrir le dossier ${f.dossier.reference}`} onClick={() => onOuvrirDossier(f.dossier.id)}>
                  <ExternalLink className="w-4 h-4 inline mr-1" aria-hidden="true" />Ouvrir le dossier
                </Bouton>
                <Bouton data-testid="bureau-amo-voir-echanges" aria-label={`Voir les échanges du dossier ${f.dossier.reference}`} onClick={() => onOuvrirDossier(f.dossier.id, { echanges: true })}>
                  <MessageSquare className="w-4 h-4 inline mr-1" aria-hidden="true" />Voir les échanges
                </Bouton>
              </div>
            )}
            {f.montants?.length > 0 ? (
              <ul className="space-y-1" data-testid="bureau-amo-montants" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {f.montants.map((m) => (
                  <li key={m.champ} className="flex justify-between gap-3" style={{ fontSize: 14, color: "var(--oh-text)" }}>
                    <span>{m.libelle}</span><span style={{ fontWeight: 500 }}>{fmtMontant(m.valeur)}</span>
                  </li>
                ))}
              </ul>
            ) : <p style={muted}>Aucun montant enregistré dans ce dossier.</p>}
            <p style={muted}>Montants lus dans OmniHub, jamais calculés.</p>
            {rattachable && f.note_dossier === "echec" && (
              <div className="space-y-2" data-testid="bureau-amo-note-echec">
                <p style={muted}>La note n'a pas pu être écrite dans ce dossier. Vous pouvez réessayer : elle ne sera écrite qu'une seule fois.</p>
                <Bouton data-testid="bureau-amo-reessayer-note" disabled={occupe} aria-label={`Réessayer la note dans le dossier ${f.dossier.reference}`}
                  onClick={() => agir(() => api.rattacher(f.id, f.dossier.id),
                    (r) => (r?.note_dossier === "ajoutee" ? "Note écrite : le mail reçu et la réponse sont notés dans le dossier." : "La note n'a pas pu être écrite, vous pouvez réessayer."))}>
                  <Link2 className="w-4 h-4 inline mr-1" aria-hidden="true" />Réessayer la note
                </Bouton>
              </div>
            )}
          </>
        ) : (
          <>
            <p style={muted} data-testid="bureau-amo-non-rattache">Ce mail n'est rattaché à aucun dossier.</p>
            {rattachable && (
              <div className="space-y-2">
                <OhField label="Chercher un dossier (référence)" htmlFor="bureau-amo-dossier-q">
                  <OhInput id="bureau-amo-dossier-q" data-testid="bureau-amo-dossier-q" value={dossierQ} placeholder="Au moins 2 caractères"
                    onChange={async (e) => {
                      const q = e.target.value;
                      setDossierQ(q);
                      if (q.trim().length < 2) { setCandidats([]); return; }
                      try { setCandidats((await api.dossiersCandidats(q.trim())).items || []); } catch { setCandidats([]); }
                    }} />
                </OhField>
                {candidats.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-3">
                    <span style={{ color: "var(--oh-text)", fontSize: 14 }}>{c.reference}</span>
                    <Bouton data-testid={`bureau-amo-rattacher-${c.id}`} disabled={occupe} aria-label={`Rattacher au dossier ${c.reference}`}
                      onClick={() => agir(() => api.rattacher(f.id, c.id),
                        (r) => (r?.note_dossier === "ajoutee" ? "Dossier rattaché : le mail reçu et la réponse sont notés dans le dossier." : "Dossier rattaché.")
                      )}>
                      <Link2 className="w-4 h-4 inline mr-1" aria-hidden="true" />Rattacher
                    </Bouton>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Section>

      <Section titre="Historique" testid="bureau-amo-journal">
        {(f.journal || []).length === 0 && <p style={muted}>Aucune action enregistrée pour l'instant.</p>}
        {(f.journal || []).map((j, i) => (
          <p key={i} style={muted}>{fmtDate(j.at)} · {libelleAction(j.action)}{j.statut_apres ? ` → ${STATUT[j.statut_apres] || "état inconnu"}` : ""}</p>
        ))}
      </Section>
    </div>
  );
}

// ───────────────────────────── Classement (CLASSER / SUPPRIMER) ─────────────────────────────
const DECISION = { CLASSER: "Classer", SUPPRIMER: "Supprimer (corbeille)" };
const STATUT_CLASSEMENT = {
  a_appliquer: "À appliquer", en_cours: "En cours", applique: "Appliqué", simule: "Simulé (rien déplacé)",
  annule: "Annulé", echec: "Échec", refuse: "Refusé à l'import",
};
const STATUT_CLASSEMENT_COULEUR = { a_appliquer: "amber", en_cours: "orange", applique: "green", simule: "cyan", annule: "blue", echec: "red", refuse: "red" };

function ClassementAmo() {
  const [etat, setEtat] = useState({ chargement: true, erreur: null, donnees: null });
  const [confirmer, setConfirmer] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const [bilan, setBilan] = useState(null);

  const charger = useCallback(async () => {
    setEtat((e) => ({ ...e, chargement: true, erreur: null }));
    try {
      setEtat({ chargement: false, erreur: null, donnees: await api.listerClassements({}) });
    } catch (e) {
      setEtat({ chargement: false, erreur: e, donnees: null });
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const appliquer = async () => {
    setOccupe(true);
    try {
      setBilan(await api.appliquerClassements({ tous: true }));
      setConfirmer(false);
      await charger();
    } catch (e) {
      toast.error(typeof e.detail === "string" ? e.detail : "Classement impossible.");
    } finally {
      setOccupe(false);
    }
  };
  const annuler = async (row) => {
    try {
      await api.annulerClassement(row.id);
      toast.success("Mail remis dans son dossier d'origine.");
      await charger();
    } catch (e) {
      toast.error(typeof e.detail === "string" ? e.detail : "Annulation impossible.");
      await charger();
    }
  };

  const colonnes = useMemo(() => [
    { key: "decision", label: "Décision", render: (v) => DECISION[v] || v },
    { key: "dossier_cible", label: "Dossier de destination", render: (v, row) => (row.decision === "SUPPRIMER" ? "Corbeille" : v || "—") },
    { key: "raison", label: "Raison", sortable: false, render: (v) => v || "—" },
    { key: "date_mail", label: "Date du mail", render: (v) => fmtDate(v) },
    { key: "statut", label: "Statut", render: (v) => <TintedBadge color={STATUT_CLASSEMENT_COULEUR[v] || "blue"}>{STATUT_CLASSEMENT[v] || v}</TintedBadge> },
  ], []);
  const rowActions = useMemo(() => [{
    label: "Annuler", icon: Undo2, onClick: annuler, show: (row) => ["applique", "simule"].includes(row.statut),
  }], []); // eslint-disable-line react-hooks/exhaustive-deps

  if (etat.erreur && [401, 403].includes(etat.erreur.status)) return <ModuleFerme />;
  const d = etat.donnees;
  const mode = d?.mode === "reel" ? "reel" : "capture";
  const aAppliquer = d?.comptes?.a_appliquer ?? 0;

  return (
    <div className="space-y-3" data-testid="bureau-amo-classement">
      <Bandeau mode={mode} />
      {etat.erreur && (
        <OhStatusNote variant="error" data-testid="bureau-amo-erreur-classement" title="Chargement impossible">Réessayez dans un instant.</OhStatusNote>
      )}
      <OhCard>
        <OhCardHeader title="Classement des mails" />
        <div className="space-y-2" style={{ marginTop: "var(--oh-space-3)" }}>
          <p style={{ ...muted, margin: 0 }} data-testid="bureau-amo-classement-compte">
            {aAppliquer} mail{aAppliquer > 1 ? "s" : ""} à classer ou à mettre à la corbeille. Rien n'est jamais supprimé définitivement : la corbeille se vide à part.
          </p>
          {!confirmer ? (
            <Bouton type="secondaire" data-testid="bureau-amo-classement-appliquer" disabled={occupe || aAppliquer === 0} onClick={() => setConfirmer(true)}>
              Appliquer le classement
            </Bouton>
          ) : (
            <div className="space-y-2" data-testid="bureau-amo-classement-confirmation">
              <OhStatusNote variant="info" title={mode === "reel" ? `Déplacer ${aAppliquer} mail(s) dans la boîte amo@ ?` : `Simuler le classement de ${aAppliquer} mail(s) ?`}>
                {mode === "reel"
                  ? "Les mails sont déplacés dans leur dossier ou à la corbeille. Chaque mail traité garde un bouton « Annuler »."
                  : "Mode capture : aucun mail n'est déplacé, le résultat est seulement simulé."}
              </OhStatusNote>
              <div className="flex flex-wrap gap-2">
                <Bouton type="secondaire" data-testid="bureau-amo-classement-confirmer" disabled={occupe} onClick={appliquer}>Confirmer le classement</Bouton>
                <Bouton data-testid="bureau-amo-classement-abandonner" disabled={occupe} onClick={() => setConfirmer(false)}>Ne pas classer</Bouton>
              </div>
            </div>
          )}
          {bilan && (
            <p style={muted} data-testid="bureau-amo-classement-bilan">
              Dernier passage : {bilan.appliques ?? 0} appliqué(s), {bilan.simules ?? 0} simulé(s), {bilan.refuses ?? 0} refusé(s), {bilan.echecs ?? 0} en échec.
            </p>
          )}
        </div>
      </OhCard>
      <h3 style={titre}>Traités</h3>
      <OHGrid
        data={d?.items || []}
        columns={colonnes}
        serverSide
        totalCount={d?.total ?? 0}
        pagination
        pageSize={d?.limite ?? 50}
        defaultSortKey="date_mail"
        defaultSortDir="desc"
        rowActions={rowActions}
        loading={etat.chargement}
        onRefresh={charger}
        emptyMessage="Aucun mail traité pour l'instant"
        testIdPrefix="bureau-amo-traites"
      />
    </div>
  );
}


// ───────────────────── Lot 3 : « À trancher », fiches de Matthieu, base de connaissance ─────────────────────
const VOIE = { 1: "Indésirable", 2: "Doute", 3: "Demande" };
const VOIE_COULEUR = { 1: "cyan", 2: "amber", 3: "blue" };
const STATUT_ENTRANT = {
  recue: "Reçue", classe: "Classé (corbeille ou spam)", a_trancher: "À trancher", tranche: "Tranchée", recherche: "Recherche en cours",
  brouillon: "Réponse prête", rappel_a_faire: "Rappel à faire", ticket: "Ticket ouvert", resolu: "Résolue",
};
const STATUT_ENTRANT_COULEUR = { recue: "blue", classe: "cyan", a_trancher: "amber", tranche: "blue", recherche: "orange", brouillon: "green", rappel_a_faire: "orange", ticket: "red", resolu: "green" };
const ETAPE = { verification_adresse: "Adresse du demandeur", base_connaissance: "Base de connaissance", dossier: "Dossier OmniHub", historique: "Historique des échanges", regles: "Règles ANAH et CEE" };
const TYPE_CONNAISSANCE = { reponse: "Réponse", regle: "Règle", decision: "Décision" };
const PREPARATION = {
  en_cours: "Matthieu prépare cette fiche.",
  brouillon: "Matthieu a préparé une réponse : elle attend votre validation. Rien n'est envoyé sans votre clic.",
  a_trancher: "Matthieu ne décide pas seul : la question vous attend dans « À trancher ».",
  analysee_sans_brouillon: "Matthieu a analysé ce mail et n'a pas préparé de réponse.",
  lead_manuel: "Demande venue du site : elle est à traiter par vous, Matthieu ne fait aucune action automatique.",
  publicite: "Publicité probable : à confirmer par vous.",
  reponse_a_un_envoi: "Réponse à l'un de nos envois : à lire par vous.",
  plafond: "Le plafond quotidien de Matthieu est atteint : aucun brouillon n'a été préparé pour cette fiche.",
  invalide: "L'analyse de Matthieu n'a pas pu être exploitée : rien n'a été préparé.",
};
const PLATEFORME_RATTACHEMENT = { numero: "par le numéro de dossier cité", client_email: "par l'adresse e-mail du client citée dans le mail" };

/** T-280 — ce que le relevé automatique a fait de la fiche. Présentation seule : l'IA ne décide jamais ici. */
function PreparationAuto({ fiche: f }) {
  const p = f.preparation;
  if (!p && !f.plateforme && !(f.pieces || []).length) return null;
  return (
    <div className="space-y-1" data-testid="bureau-amo-preparation">
      {p && <p style={{ color: "var(--oh-text)", fontSize: 14, margin: 0 }} data-testid="bureau-amo-preparation-etat">{PREPARATION[p.etat] || "Préparation en cours d'examen."}</p>}
      {p?.action?.libelle && <p style={muted} data-testid="bureau-amo-preparation-action">Action envisagée (non exécutée) : {p.action.libelle}</p>}
      {p?.montant_ecarte && <p style={muted} data-testid="bureau-amo-preparation-montant">Un montant que le mail ne contenait pas a été écarté du brouillon.</p>}
      {f.plateforme && (
        <p style={muted} data-testid="bureau-amo-preparation-plateforme">
          Mail d'une plateforme{f.rattache_par ? ` rattaché au client ${PLATEFORME_RATTACHEMENT[f.rattache_par] || ""}` : " : aucun client rattaché"}.
        </p>
      )}
      {(f.pieces || []).length > 0 && <p style={muted} data-testid="bureau-amo-preparation-pieces">{f.pieces.length} pièce{f.pieces.length > 1 ? "s" : ""} jointe{f.pieces.length > 1 ? "s" : ""} (non lue{f.pieces.length > 1 ? "s" : ""} par Matthieu).</p>}
    </div>
  );
}

const LEAD_SOURCE = { site_simulateur: "Site – simulateur", site_contact: "Site – contact" };
const LEAD_RANGEMENT = { ok: "Mail rangé dans Leads", simule: "Rangement simulé (mode capture)", refuse: "Rangement refusé", echec: "Rangement à refaire" };
const SOURCE_CONNAISSANCE = { validation: "Validation de JJ", a_trancher: "Choix « À trancher »", ticket: "Ticket résolu" };

function messageErreur(e, defaut) {
  return typeof e?.detail === "string" ? e.detail : defaut;
}

function ATrancher({ onOuvrirProposition }) {
  const [etat, setEtat] = useState({ chargement: true, erreur: null, donnees: null });
  const [occupe, setOccupe] = useState(null);
  const connecte = useMatthieuConnecte();

  const charger = useCallback(async () => {
    setEtat((e) => ({ ...e, chargement: true, erreur: null }));
    try {
      setEtat({ chargement: false, erreur: null, donnees: await api.listerATrancher({}) });
    } catch (e) {
      setEtat({ chargement: false, erreur: e, donnees: null });
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const choisir = async (item, choix) => {
    setOccupe(item.id);
    try {
      await api.repondreATrancher(item.id, item.version, choix.id);
      toast.success("Noté : Matthieu s'en sert pour les prochains mails.");
    } catch (e) {
      toast.error(e.status === 409 ? "Cette question a changé entre-temps : la liste est rechargée." : messageErreur(e, "Réponse non enregistrée."));
    } finally {
      setOccupe(null);
      await charger();
    }
  };

  if (etat.erreur && [401, 403].includes(etat.erreur.status)) return <ModuleFerme />;
  const items = etat.donnees?.items || [];
  return (
    <div className="space-y-3" data-testid="bureau-amo-a-trancher">
      {etat.erreur && (
        <OhStatusNote variant="error" data-testid="bureau-amo-erreur-trancher" title="Chargement impossible">Réessayez dans un instant.</OhStatusNote>
      )}
      {!etat.chargement && !etat.erreur && items.length === 0 && (
        <p style={muted} data-testid="bureau-amo-trancher-vide">Rien à trancher : Matthieu a tout traité.</p>
      )}
      {items.map((it) => (
        <OhCard key={it.id} data-testid={`bureau-amo-trancher-${it.id}`}>
          <OhCardHeader title={it.question} />
          <div className="space-y-2" style={{ marginTop: "var(--oh-space-3)" }}>
            <p style={muted}>
              {it.objet} · {it.expediteur_adresse} · {fmtDate(it.date_mail)} · confiance {it.confiance} %
            </p>
            {it.ticket_id && <p style={muted}>Un ticket est ouvert pour ce doute.</p>}
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Choix possibles">
              {(it.choix || []).map((c) => (
                <Bouton key={c.id} type="secondaire" data-testid={`bureau-amo-choix-${it.id}-${c.id}`} disabled={occupe === it.id}
                  onClick={() => choisir(it, c)}>
                  {c.libelle}
                </Bouton>
              ))}
            </div>
            <ActionsEntrant f={{ ...it, proposition_id: it.proposition_id }} connecte={connecte} ouverture={occupe === it.id} setOuverture={(v) => setOccupe(v ? it.id : null)}
              occupe={occupe === it.id} onOuvrirProposition={onOuvrirProposition} onChange={charger} />
            <p style={muted}>Fiche de Matthieu</p>
          </div>
        </OhCard>
      ))}
    </div>
  );
}

const REDACTION_POSSIBLE = ["recue", "a_trancher", "tranche", "ticket", "brouillon"];

// Actions communes aux fiches entrantes : réponse (Matthieu), transfert, et pour les publicités la règle « toujours classer ».
function ActionsEntrant({ f, connecte, ouverture, setOuverture, occupe, onOuvrirProposition, onChange }) {
  const peutRediger = !f.proposition_id && !f.source && !f.sans_message && REDACTION_POSSIBLE.includes(f.statut);
  const rediger = async () => {
    setOuverture(true);
    try {
      const r = await api.rediger(f.id, f.version);
      toast.success(r.deja ? "Une réponse existe déjà pour ce mail : elle est ouverte." : "Réponse ouverte : Matthieu la rédige, rien n'est envoyé.");
      if (onOuvrirProposition) onOuvrirProposition(r.proposition_id, true);
    } catch (e) {
      toast.error(messageEchec(e, "La réponse n'a pas pu être ouverte."));
    } finally {
      setOuverture(false);
    }
  };
  return (
    <div className="space-y-3" data-testid="bureau-amo-entrant-actions">
      {peutRediger && <BoutonRediger connecte={connecte} occupe={ouverture || occupe} onClick={rediger} testid="bureau-amo-entrant-rediger" />}
      {f.proposition_id && (
        <Bouton type="secondaire" data-testid="bureau-amo-entrant-ouvrir-reponse" onClick={() => onOuvrirProposition && onOuvrirProposition(f.proposition_id, false)}>
          Ouvrir la réponse
        </Bouton>
      )}
      {f.est_publicite && f.statut !== "classe" && <ToujoursClasser fiche={f} desactive={occupe} onFait={onChange} />}
      <Transferer type="entrants" id={f.id} transfereA={f.transfere_a} desactive={occupe || f.statut === "classe"} onFait={onChange} />
    </div>
  );
}

function FicheMatthieu({ id, onFerme, onChange, onOuvrirProposition }) {
  const [f, setF] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [reponse, setReponse] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [ouverture, setOuverture] = useState(false);
  const connecte = useMatthieuConnecte(Boolean(f) && !f.proposition_id);

  const charger = useCallback(async () => {
    try { setF(await api.ficheEntrant(id)); setErreur(null); } catch (e) { setErreur(e); }
  }, [id]);
  useEffect(() => { charger(); }, [charger]);

  const agir = async (action, succes) => {
    setOccupe(true);
    try {
      await action();
      toast.success(succes);
    } catch (e) {
      toast.error(e.status === 409 ? "La fiche a changé entre-temps : elle est rechargée." : messageErreur(e, "Action impossible."));
    } finally {
      setOccupe(false);
      await charger();
      onChange();
    }
  };

  if (erreur) return <OhStatusNote variant="error" data-testid="bureau-amo-erreur-entrant" title="Fiche introuvable">Cette fiche n'est plus disponible.</OhStatusNote>;
  if (!f) return <p style={muted}>Chargement…</p>;
  return (
    <OhCard data-testid="bureau-amo-entrant">
      <OhCardHeader title={f.objet} />
      <div className="space-y-2" style={{ marginTop: "var(--oh-space-3)" }}>
        <div className="flex flex-wrap items-center gap-2">
          <TintedBadge color={VOIE_COULEUR[f.voie] || "blue"}>{f.voie ? `Voie ${f.voie} · ${VOIE[f.voie] || ""}` : "Pas encore analysée"}</TintedBadge>
          <TintedBadge color={STATUT_ENTRANT_COULEUR[f.statut] || "blue"} data-testid="bureau-amo-entrant-statut">{STATUT_ENTRANT[f.statut] || f.statut}</TintedBadge>
        </div>
        <p style={muted} data-testid="bureau-amo-entrant-signature">Fiche de Matthieu{f.confiance != null ? ` · confiance ${f.confiance} %` : ""} · {f.expediteur_adresse} · {fmtDate(f.date_mail)}</p>
        {f.raison && <p style={{ color: "var(--oh-text)", fontSize: 14, margin: 0 }} data-testid="bureau-amo-entrant-raison">{f.raison}</p>}
        <PreparationAuto fiche={f} />
        {f.voie != null && f.voie_proposee !== f.voie && <p style={muted}>Matthieu proposait la voie {f.voie_proposee} ; le serveur l'a ramenée à la voie {f.voie} par prudence.</p>}
        {f.demande && <p style={{ color: "var(--oh-text)", fontSize: 14, margin: 0 }}><span style={titre}>Demande : </span>{f.demande}</p>}
        {f.source === "formulaire_interesse" && f.rappel_numero && (
          <div className="space-y-1" data-testid="bureau-amo-entrant-rappel-formulaire">
            <p style={{ color: "var(--oh-text)", fontSize: 14, margin: 0 }} data-testid="bureau-amo-entrant-rappel-nom">
              <span style={titre}>À rappeler : </span>{f.nom_appelant || f.expediteur_adresse}
            </p>
            <p style={{ color: "var(--oh-text)", fontSize: 14, margin: 0 }} data-testid="bureau-amo-entrant-rappel-numero">
              <span style={titre}>Numéro à rappeler : </span>{f.rappel_numero}
            </p>
            {f.telephone_fiche && f.telephone_fiche !== f.rappel_numero && (
              <p style={muted} data-testid="bureau-amo-entrant-rappel-numero-fiche">Numéro différent de celui de la fiche client ({f.telephone_fiche}).</p>
            )}
            {f.mises_a_jour > 0 && <p style={muted}>Coordonnées corrigées par la personne ({f.mises_a_jour} fois).</p>}
            {f.opposition_prospection && (
              <OhStatusNote variant="info" title="Opposé à la prospection" data-testid="bureau-amo-entrant-opposition">
                {f.note_opposition || "Rappel à sa demande uniquement."}
              </OhStatusNote>
            )}
          </div>
        )}
        {f.lead && (
          <div className="space-y-1" data-testid="bureau-amo-entrant-lead">
            <p style={{ color: "var(--oh-text)", fontSize: 14, margin: 0 }}>
              <span style={titre}>Lead du site : </span>{LEAD_SOURCE[f.lead.source] || f.lead.source}
              {f.lead.prospect_id ? ` · prospect ${f.lead.prospect_action === "cree" ? "créé" : "mis à jour"}` : " · aucun prospect créé"}
            </p>
            {f.lead.rappel_a_faire && (
              <OhStatusNote variant="info" title="Rappel à faire" data-testid="bureau-amo-entrant-rappel">
                L'adresse e-mail est inutilisable : rappelez le demandeur au téléphone (numéro dans sa fiche prospect).
              </OhStatusNote>
            )}
            {f.lead.rangement && <p style={muted} data-testid="bureau-amo-entrant-rangement">{LEAD_RANGEMENT[f.lead.rangement] || f.lead.rangement}</p>}
          </div>
        )}
        {(f.recherches || []).length > 0 && (
          <ul style={{ margin: 0, paddingLeft: "var(--oh-space-4)", color: "var(--oh-text)", fontSize: 14 }} data-testid="bureau-amo-entrant-recherches">
            {f.recherches.map((r) => <li key={r.etape}>{ETAPE[r.etape] || r.etape} : {r.trouve ? `${r.nombre} trouvé` : "rien trouvé"}</li>)}
          </ul>
        )}
        {f.statut === "classe" && (
          <Bouton type="secondaire" data-testid="bureau-amo-annuler-voie1" disabled={occupe}
            onClick={() => agir(() => api.annulerVoie1(f.id, f.version), "Le mail est remis dans la boîte de réception.")}>
            Annuler le déplacement
          </Bouton>
        )}
        {["recue", "recherche"].includes(f.statut) && f.voie === 3 && (
          <Bouton type="secondaire" data-testid="bureau-amo-relancer" disabled={occupe}
            onClick={() => agir(() => api.relancerEntrant(f.id, f.version), "Recherche relancée.")}>
            Relancer la recherche
          </Bouton>
        )}
        {f.statut === "ticket" && (
          <div className="space-y-2" data-testid="bureau-amo-resoudre">
            <OhStatusNote variant="info" title="Matthieu n'a pas trouvé la réponse">
              Un ticket est ouvert. Écrivez la réponse : elle part dans la file de validation et entre dans la base de connaissance.
            </OhStatusNote>
            <OhField label="Réponse à proposer" htmlFor="bureau-amo-resoudre-texte">
              <textarea id="bureau-amo-resoudre-texte" data-testid="bureau-amo-resoudre-texte" value={reponse} rows={6}
                onChange={(e) => setReponse(e.target.value)}
                style={{ width: "100%", padding: "var(--oh-space-3)", borderRadius: "var(--oh-radius-control)", border: "0.5px solid var(--oh-border)", background: "var(--oh-bg-elevated)", color: "var(--oh-text)", fontSize: 14, lineHeight: 1.6 }} />
            </OhField>
            <Bouton type="secondaire" data-testid="bureau-amo-resoudre-envoyer" disabled={occupe || !reponse.trim()}
              onClick={() => agir(() => api.resoudreEntrant(f.id, f.version, reponse.trim()), "Réponse préparée : elle attend votre validation.")}>
              Enregistrer la réponse
            </Bouton>
          </div>
        )}
        <ActionsEntrant f={f} connecte={connecte} ouverture={ouverture} setOuverture={setOuverture} occupe={occupe}
          onOuvrirProposition={onOuvrirProposition} onChange={async () => { await charger(); onChange(); }} />
        <Bouton data-testid="bureau-amo-entrant-fermer" onClick={onFerme}>Fermer la fiche</Bouton>
      </div>
    </OhCard>
  );
}

function FichesMatthieu({ onOuvrirProposition }) {
  const [requete, setRequete] = useState({ statut: "", page: 1, limite: 50 });
  const [etat, setEtat] = useState({ chargement: true, erreur: null, donnees: null });
  const [ouvert, setOuvert] = useState(null);

  const charger = useCallback(async () => {
    setEtat((e) => ({ ...e, chargement: true, erreur: null }));
    try {
      setEtat({ chargement: false, erreur: null, donnees: await api.listerEntrants(requete) });
    } catch (e) {
      setEtat({ chargement: false, erreur: e, donnees: null });
    }
  }, [requete]);
  useEffect(() => { charger(); }, [charger]);

  const colonnes = useMemo(() => [
    { key: "voie", label: "Voie", render: (v) => (v ? <TintedBadge color={VOIE_COULEUR[v] || "blue"}>{VOIE[v] || v}</TintedBadge> : <TintedBadge color="blue">À analyser</TintedBadge>) },
    { key: "objet", label: "Objet du mail", sortable: false },
    { key: "expediteur_adresse", label: "Expéditeur", sortable: false },
    { key: "confiance", label: "Confiance", render: (v) => (v == null ? "—" : `${v} %`) },
    { key: "statut", label: "Statut", render: (v) => <TintedBadge color={STATUT_ENTRANT_COULEUR[v] || "blue"}>{STATUT_ENTRANT[v] || v}</TintedBadge> },
    { key: "created_at", label: "Reçue le", render: (v) => fmtDate(v) },
  ], []);
  const rowActions = useMemo(() => [{ label: "Ouvrir la fiche", icon: Eye, onClick: (row) => setOuvert(row.id) }], []);
  const bulkActions = useMemo(() => [{
    label: "Exporter la sélection", icon: Download,
    onClick: (ids) => {
      const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const lignes = (etat.donnees?.items || []).filter((i) => ids.includes(i.id))
        .map((l) => [l.objet, l.voie, l.confiance, l.statut, l.created_at].map(esc).join(";"));
      const contenu = [["Objet", "Voie", "Confiance", "Statut", "Reçue le"].map(esc).join(";"), ...lignes].join("\n");
      const url = URL.createObjectURL(new Blob([contenu], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = "fiches-matthieu-selection.csv"; a.click();
      URL.revokeObjectURL(url);
    },
  }], [etat.donnees]);

  if (etat.erreur && [401, 403].includes(etat.erreur.status)) return <ModuleFerme />;
  return (
    <div className="space-y-3" data-testid="bureau-amo-fiches">
      {etat.erreur && (
        <OhStatusNote variant="error" data-testid="bureau-amo-erreur-fiches" title="Chargement impossible">Réessayez dans un instant.</OhStatusNote>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <OhField label="Afficher" htmlFor="bureau-amo-fiches-statut" style={{ minWidth: 220 }}>
          <OhSelect id="bureau-amo-fiches-statut" data-testid="bureau-amo-fiches-statut" value={requete.statut}
            onChange={(e) => setRequete((r) => ({ ...r, statut: e.target.value, page: 1 }))}>
            <option value="">Toutes les fiches</option>
            {Object.entries(STATUT_ENTRANT).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </OhSelect>
        </OhField>
      </div>
      {ouvert && <FicheMatthieu id={ouvert} onFerme={() => setOuvert(null)} onChange={charger} onOuvrirProposition={onOuvrirProposition} />}
      <OHGrid
        data={etat.donnees?.items || []}
        columns={colonnes}
        serverSide
        totalCount={etat.donnees?.total ?? 0}
        pagination
        pageSize={requete.limite}
        onPageChange={({ page, pageSize }) => setRequete((r) => ({ ...r, page, limite: pageSize }))}
        defaultSortKey="created_at"
        defaultSortDir="desc"
        rowActions={rowActions}
        bulkActions={bulkActions}
        selectable
        onRowClick={(row) => setOuvert(row.id)}
        loading={etat.chargement}
        onRefresh={charger}
        emptyMessage="Aucune fiche de Matthieu pour l'instant"
        testIdPrefix="bureau-amo-fiches-grille"
      />
    </div>
  );
}

function Connaissance() {
  const [type, setType] = useState("");
  const [etat, setEtat] = useState({ chargement: true, erreur: null, donnees: null });
  const charger = useCallback(async () => {
    setEtat((e) => ({ ...e, chargement: true, erreur: null }));
    try {
      setEtat({ chargement: false, erreur: null, donnees: await api.listerConnaissances({ type }) });
    } catch (e) {
      setEtat({ chargement: false, erreur: e, donnees: null });
    }
  }, [type]);
  useEffect(() => { charger(); }, [charger]);

  const colonnes = useMemo(() => [
    { key: "question", label: "Question", sortable: false },
    { key: "reponse", label: "Réponse", sortable: false },
    { key: "type", label: "Type", render: (v) => TYPE_CONNAISSANCE[v] || v },
    { key: "source", label: "Source", render: (v) => SOURCE_CONNAISSANCE[v] || v },
    { key: "usages", label: "Usages" },
    { key: "validee_par", label: "Validée par", sortable: false, render: (v) => v || "—" },
  ], []);

  if (etat.erreur && [401, 403].includes(etat.erreur.status)) return <ModuleFerme />;
  return (
    <div className="space-y-3" data-testid="bureau-amo-connaissance">
      <p style={{ ...muted, margin: 0 }}>Ce que Matthieu a appris : réponses validées, choix « À trancher » et tickets résolus. Pendant le rodage, rien n'est rejoué sans votre validation.</p>
      {etat.erreur && (
        <OhStatusNote variant="error" data-testid="bureau-amo-erreur-connaissance" title="Chargement impossible">Réessayez dans un instant.</OhStatusNote>
      )}
      <OhField label="Type de fiche" htmlFor="bureau-amo-connaissance-type" style={{ maxWidth: 220 }}>
        <OhSelect id="bureau-amo-connaissance-type" data-testid="bureau-amo-connaissance-type" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">Tous les types</option>
          {Object.entries(TYPE_CONNAISSANCE).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </OhSelect>
      </OhField>
      <OHGrid
        data={etat.donnees?.items || []}
        columns={colonnes}
        serverSide
        totalCount={etat.donnees?.total ?? 0}
        pagination
        pageSize={etat.donnees?.limite ?? 50}
        defaultSortKey="usages"
        defaultSortDir="desc"
        loading={etat.chargement}
        onRefresh={charger}
        emptyMessage="La base de connaissance est vide pour l'instant"
        testIdPrefix="bureau-amo-connaissance-grille"
      />
    </div>
  );
}

// ───────────────────────── Import des fichiers préparés par oh-amo ─────────────────────────
function ImportAmo() {
  const [occupe, setOccupe] = useState(false);
  const [resultat, setResultat] = useState(null);

  const importer = async (fichier, appel, nom) => {
    if (!fichier) return;
    setOccupe(true);
    setResultat(null);
    try {
      const r = await appel(await fichier.text());
      setResultat({ nom, ok: true, r });
    } catch (e) {
      setResultat({ nom, ok: false, detail: typeof e.detail === "string" ? e.detail : "Import refusé." });
    } finally {
      setOccupe(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="bureau-amo-import">
      <OhCard>
        <OhCardHeader title="Importer les fiches préparées" />
        <div className="space-y-2" style={{ marginTop: "var(--oh-space-3)" }}>
          <p style={{ ...muted, margin: 0 }}>Fichier de propositions préparé par oh-amo (JSON, ou une fiche par ligne). Les fiches déjà importées sont ignorées.</p>
          <OhField label="Fichier des propositions" htmlFor="bureau-amo-import-propositions">
            <input id="bureau-amo-import-propositions" data-testid="bureau-amo-import-propositions" type="file" accept=".json,.jsonl,application/json" disabled={occupe}
              onChange={(e) => importer(e.target.files?.[0], api.importerPropositions, "propositions")} />
          </OhField>
        </div>
      </OhCard>
      <OhCard>
        <OhCardHeader title="Importer le plan de classement" />
        <div className="space-y-2" style={{ marginTop: "var(--oh-space-3)" }}>
          <p style={{ ...muted, margin: 0 }}>Fichier amo-plan-classement.jsonl. Importer ne déplace rien : le classement s'applique ensuite depuis l'onglet « Classement ».</p>
          <OhField label="Fichier du plan de classement" htmlFor="bureau-amo-import-plan">
            <input id="bureau-amo-import-plan" data-testid="bureau-amo-import-plan" type="file" accept=".jsonl,.json" disabled={occupe}
              onChange={(e) => importer(e.target.files?.[0], api.importerPlan, "plan")} />
          </OhField>
        </div>
      </OhCard>
      <OhCard>
        <OhCardHeader title="Importer les mails entrants" />
        <div className="space-y-2" style={{ marginTop: "var(--oh-space-3)" }}>
          <p style={{ ...muted, margin: 0 }}>Fichier JSON des mails lus par Matthieu. Chaque mail crée sa fiche ; les mails déjà reçus sont ignorés.</p>
          <OhField label="Fichier des mails entrants" htmlFor="bureau-amo-import-entrants">
            <input id="bureau-amo-import-entrants" data-testid="bureau-amo-import-entrants" type="file" accept=".json,application/json" disabled={occupe}
              onChange={(e) => importer(e.target.files?.[0], api.importerEntrants, "mails entrants")} />
          </OhField>
        </div>
      </OhCard>
      {resultat && (
        <OhStatusNote variant={resultat.ok ? "success" : "error"} data-testid="bureau-amo-import-resultat"
          title={resultat.ok ? `Import du ${resultat.nom} terminé` : `Import du ${resultat.nom} refusé`}>
          {resultat.ok
            ? Object.entries(resultat.r).filter(([, v]) => typeof v === "number" || Array.isArray(v)).map(([k, v]) => `${k.replace(/_/g, " ")} : ${Array.isArray(v) ? v.length : v}`).join(" · ")
            : resultat.detail}
        </OhStatusNote>
      )}
    </div>
  );
}

// ───────────────────────────────── Entrée ─────────────────────────────────────
export default function BureauAmo({ onOuvrirWebmail, onOuvrirDossier, vueInitiale = "pretes", filtreInitial, avecFiches = true, reglages = false, superAdmin = false, onOuvrirIntegrations }) {   // T-467 : `reglages` = page de réglages de Matthieu (jamais le Bureau)
  const [ouvert, setOuvert] = useState(null);
  const [vue, setVue] = useState(vueInitiale);
  const [parler, setParler] = useState(false);
  const [nbPretes, setNbPretes] = useState(null);
  const [redactionPour, setRedactionPour] = useState(null);
  const ouvrirProposition = (pid, redaction = false) => { setRedactionPour(redaction ? pid : null); setOuvert(pid); setVue("reponses"); };
  const VUES_REGLAGES = [["connaissance", "Connaissance"], ["regles", "Règles apprises"], ["classement", "Classement"], ["import", "Importer"], ...(superAdmin ? [["moteur", "Moteur"]] : [])];
  const VUES = reglages ? VUES_REGLAGES : [["pretes", nbPretes === null ? "Réponses prêtes" : `Réponses prêtes (${nbPretes})`], ["reponses", "Réponses"], ["a-trancher", "À trancher"],
    ...(avecFiches ? [["fiches", "Fiches de Matthieu"]] : []),   // T-462 : « Réglages de Matthieu » n'a plus cette grille (doublon de « Tous les mails ») ; elle vit dans Outils avancés
    ["connaissance", "Connaissance"], ["classement", "Classement"], ["import", "Importer"]];
  const ouvrirFiche = (id) => ouvrirProposition(id);
  return (
    <div data-testid="bureau-amo">
      <div className="flex items-center gap-3" style={{ marginBottom: "var(--oh-space-4)" }}>
        <Inbox className="w-5 h-5" style={{ color: "var(--oh-accent-text)" }} />
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 500, color: "var(--oh-text)" }} data-testid="bureau-amo-titre">{reglages ? "Réglages de Matthieu" : "Bureau de Matthieu"}</h2>
          <p style={{ ...muted, margin: 0 }}>{reglages ? "Ce que Matthieu sait et comment il range les mails. Le travail de tous les jours se fait dans « Bureau de Matthieu »." : "l'agent AMO : une fiche par mail, la réponse prête, ce qui vous attend"}</p>
        </div>
        {!ouvert && !reglages && (
          <Bouton type="secondaire" data-testid="bureau-amo-parler-general" style={{ marginLeft: "auto" }} onClick={() => setParler(true)}>
            <MessageSquare className="w-4 h-4 inline mr-1" aria-hidden="true" />Parler à Matthieu
          </Bouton>
        )}
      </div>
      {parler && !ouvert && !reglages && <ParlerAMatthieu cible={{ type: "general" }} onFerme={() => setParler(false)} />}
      {!ouvert && !reglages && <NouveauxMails />}
      <div className="flex flex-wrap gap-2" role="tablist" style={{ marginBottom: "var(--oh-space-3)" }}>
        {VUES.map(([k, l]) => (
          <Bouton key={k} type={vue === k ? "secondaire" : "discret"} role="tab" aria-selected={vue === k} data-testid={`bureau-amo-vue-${k}`} onClick={() => setVue(k)}>{l}</Bouton>
        ))}
      </div>
      {vue === "pretes" ? <ReponsesPretes MailRecu={MailRecu} onOuvrir={ouvrirFiche} onOuvrirWebmail={onOuvrirWebmail} onCompte={setNbPretes} />
        : vue === "a-trancher" ? <ATrancher onOuvrirProposition={ouvrirProposition} />
        : vue === "fiches" ? <FichesMatthieu onOuvrirProposition={ouvrirProposition} />
        : vue === "regles" ? <SavoirMatthieu modifiable section="regles" />
        : vue === "moteur" ? (
          <div className="space-y-3" data-testid="bureau-amo-moteur">
            <p style={{ ...muted, margin: 0 }}>Le moteur de Matthieu (quel modèle d'IA le fait fonctionner) se règle une seule fois, dans Gouvernance › Intégrations › Matthieu, par le super administrateur.</p>
            {onOuvrirIntegrations && <Bouton type="secondaire" data-testid="bureau-amo-moteur-ouvrir" onClick={onOuvrirIntegrations}>Ouvrir Intégrations › Matthieu</Bouton>}
          </div>)
        : vue === "connaissance" ? (reglages ? <SavoirMatthieu modifiable section="connaissances" /> : <Connaissance />)
        : vue === "classement" ? <ClassementAmo />
        : vue === "import" ? <ImportAmo />
        : reglages ? <SavoirMatthieu modifiable section="connaissances" />      // T-486 : la MÊME lecture que l'onglet du Bureau, modifiable ici ; T-467 : jamais le Bureau dans « Réglages de Matthieu »
        : <ListeAmo ouvertId={ouvert} onOuvrir={(id) => { setRedactionPour(null); setOuvert(id); }} onFermer={() => setOuvert(null)}
            onOuvrirProposition={ouvrirProposition} redactionPour={redactionPour} filtreInitial={filtreInitial}
            onOuvrirATrancher={() => setVue("a-trancher")} onOuvrirWebmail={onOuvrirWebmail} onOuvrirDossier={onOuvrirDossier} />}
    </div>
  );
}

export { MailRecu, ListeAmo, FicheAmo, ClassementAmo, ImportAmo, ATrancher, FichesMatthieu, Connaissance };
